import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import Logo from '../../../assets/logo.png';
import useErrors from '../../../hooks/useErrors';
import server from '../../../services/server';
import { useAuth } from '../../../context/AuthContext';
import { isAdminUser } from '../../../services/authSession';
import { adminTeamLabel } from './adminTeams';

const registerSchema = yup.object().shape({
    password: yup.string().min(8, 'Mínimo de 8 caracteres').required('Informe a senha'),
    confirmPassword: yup
        .string()
        .required('Confirme a senha')
        .oneOf([yup.ref('password')], 'As senhas devem ser iguais'),
});

const loginSchema = yup.object().shape({
    password: yup.string().required('Informe a senha'),
});

function InviteSummary({ invite }) {
    return (
        <Stack spacing={0.5} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                Convite para o admin Eurobem
            </Typography>
            <Typography fontWeight={700}>Cargo: {invite.teamLabel || adminTeamLabel(invite.team)}</Typography>
            <Typography variant="body2" color="text.secondary">
                Convidado: <strong>{invite.email}</strong>
            </Typography>
        </Stack>
    );
}

export default function AdminInviteAccept() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { Login, LogOut, user } = useAuth();
    const { setErrors, enqueueSnackbar } = useErrors();
    const [invite, setInvite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mode, setMode] = useState('register');

    const sessionEmail = String(user?.userName || '').trim().toLowerCase();
    const inviteEmail = String(invite?.email || '').trim().toLowerCase();
    const isLoggedIn = Boolean(user?.id);
    const inviteIsForSomeoneElse = useMemo(() => {
        if (!isLoggedIn || !inviteEmail) return false;
        return sessionEmail !== inviteEmail;
    }, [isLoggedIn, sessionEmail, inviteEmail]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const { data } = await server.get(`admin-invites/${token}`);
                if (cancelled) return;
                const payload = data?.data;
                setInvite(payload);
                setMode(payload?.userExists ? 'login' : 'register');
            } catch (error) {
                if (!cancelled) setErrors(error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const finishAccept = async body => {
        const { data } = await server.post(`admin-invites/${token}/accept`, body);
        const result = data?.data;
        await Login({
            token: result.token,
            refreshtoken: result.refreshToken,
            user: result.user,
        });
        enqueueSnackbar('Convite aceito. Bem-vindo!');
        navigate('/dashboard', { replace: true });
    };

    const registerForm = useFormik({
        initialValues: { password: '', confirmPassword: '' },
        validationSchema: registerSchema,
        onSubmit: async values => {
            try {
                await finishAccept({
                    mode: 'register',
                    password: values.password,
                });
            } catch (error) {
                setErrors(error);
            }
        },
    });

    const loginForm = useFormik({
        initialValues: { password: '' },
        validationSchema: loginSchema,
        onSubmit: async values => {
            try {
                await finishAccept({
                    mode: 'login',
                    password: values.password,
                });
            } catch (error) {
                setErrors(error);
            }
        },
    });

    if (loading) {
        return (
            <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
                <Typography color="text.secondary">Carregando convite…</Typography>
            </Box>
        );
    }

    if (!invite) {
        return (
            <Box maxWidth={420} mx="auto" px={3} py={8}>
                <Alert severity="error">
                    <AlertTitle>Convite inválido</AlertTitle>
                    Este link não é válido ou expirou.
                </Alert>
                <Button component={Link} to="/" sx={{ mt: 2 }}>
                    Ir para o login
                </Button>
            </Box>
        );
    }

    if (!invite.canAccept) {
        return (
            <Box maxWidth={420} mx="auto" px={3} py={8}>
                <Box component="img" src={Logo} alt="Eurobem" sx={{ height: 40, mb: 3 }} />
                <InviteSummary invite={invite} />
                <Alert sx={{ mt: 2 }}>
                    <AlertTitle>Convite indisponível</AlertTitle>
                    Status: {invite.status}. Solicite um novo convite à equipe Eurobem.
                </Alert>
                <Button component={Link} to="/" sx={{ mt: 2 }}>
                    Ir para o login
                </Button>
            </Box>
        );
    }

    if (inviteIsForSomeoneElse) {
        return (
            <Box maxWidth={420} mx="auto" px={3} py={8}>
                <Box component="img" src={Logo} alt="Eurobem" sx={{ height: 40, mb: 3 }} />
                <Card variant="outlined">
                    <CardHeader
                        title="Este convite não é para você"
                        subheader="Você está logado com outra conta. Para aceitar, saia e use o e-mail do convite."
                    />
                    <CardContent>
                        <Stack spacing={2}>
                            <InviteSummary invite={invite} />
                            <Alert severity="error">
                                O convite é para <strong>{invite.email}</strong>, mas você está como{' '}
                                <strong>{user?.userName}</strong>.
                            </Alert>
                            <Stack direction="row" spacing={1}>
                                {isAdminUser(user) ? (
                                    <Button variant="outlined" component={Link} to="/dashboard" fullWidth>
                                        Voltar ao painel
                                    </Button>
                                ) : (
                                    <Button variant="outlined" component={Link} to="/" fullWidth>
                                        Voltar
                                    </Button>
                                )}
                                <Button color="error" variant="contained" onClick={LogOut} fullWidth>
                                    Sair
                                </Button>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    return (
        <Box maxWidth={420} mx="auto" px={3} py={8}>
            <Box component="img" src={Logo} alt="Eurobem" sx={{ height: 40, mb: 3 }} />
            <Card variant="outlined">
                <CardHeader
                    title="Aceitar convite"
                    subheader="Confirme os dados abaixo para entrar no admin Eurobem."
                />
                <CardContent>
                    <Stack spacing={2}>
                        <InviteSummary invite={invite} />
                        <Tabs value={mode} onChange={(_, next) => setMode(next)} variant="fullWidth">
                            <Tab value="register" label="Criar conta" disabled={invite.userExists} />
                            <Tab value="login" label="Já tenho conta" />
                        </Tabs>

                        {mode === 'register' ? (
                            <Stack component="form" onSubmit={registerForm.handleSubmit} spacing={2}>
                                <TextField
                                    label="Senha"
                                    name="password"
                                    type="password"
                                    autoComplete="new-password"
                                    value={registerForm.values.password}
                                    onChange={registerForm.handleChange}
                                    error={
                                        registerForm.touched.password && Boolean(registerForm.errors.password)
                                    }
                                    helperText={registerForm.touched.password && registerForm.errors.password}
                                    fullWidth
                                />
                                <TextField
                                    label="Confirmar senha"
                                    name="confirmPassword"
                                    type="password"
                                    autoComplete="new-password"
                                    value={registerForm.values.confirmPassword}
                                    onChange={registerForm.handleChange}
                                    error={
                                        registerForm.touched.confirmPassword &&
                                        Boolean(registerForm.errors.confirmPassword)
                                    }
                                    helperText={
                                        registerForm.touched.confirmPassword &&
                                        registerForm.errors.confirmPassword
                                    }
                                    fullWidth
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={registerForm.isSubmitting}
                                >
                                    Criar conta e aceitar
                                </Button>
                            </Stack>
                        ) : (
                            <Stack component="form" onSubmit={loginForm.handleSubmit} spacing={2}>
                                <TextField label="E-mail" value={invite.email} disabled fullWidth />
                                <TextField
                                    label="Senha"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={loginForm.values.password}
                                    onChange={loginForm.handleChange}
                                    error={loginForm.touched.password && Boolean(loginForm.errors.password)}
                                    helperText={loginForm.touched.password && loginForm.errors.password}
                                    fullWidth
                                />
                                <Button type="submit" variant="contained" disabled={loginForm.isSubmitting}>
                                    Entrar e aceitar
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
}
